import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { Simplify } from 'type-fest';

import { plural, pluralizeIssues } from '../../../../../../shared/helpers';
import { COUNT_MAX_FE, isProjectScope, JIRA_CONTENT_MAX_LENGTH } from '../../../../../../shared/issuesV2';
import { window } from '../../../../../globals';
import { useOnMount } from '../../../../../hooks';
import { useCurrentProject } from '../../../../../hooks/api/projects/useCurrentProject';
import * as IssuesV2Store from '../../../../../redux/issuesV2';
import OrganizationsModule from '../../../../../redux/organizations';
import { nuc } from '../../../../../utils';
import { Icon, icons, RouterLink } from '../../../../generic';
import FormGroup from '../../../../generic/Forms/FormGroup';
import LabeledTextArea from '../../../../generic/Forms/LabeledTextArea';
import LabeledTextInput from '../../../../generic/Forms/LabeledTextInput';
import Text from '../../../../generic/Text';
import * as BuildTicket from '../buildTicketContent';
import ExportIssueModalWarning from '../ExportIssueModalWarning';
import ModalIssueExportFields from '../ModalIssueExportFields';
import useJiraExport from '../useJiraExport';
import useTrackerExportPicker from '../useTrackerExportPicker';
import useUniqueTicketEditor from '../useUniqueTicketEditor';
import type { AnyExportRequest, Category, Issue } from '../../../../../../shared/issuesV2';
import type { Jira } from '../../../../../../shared/model-typings';
import type { Project, SetState, User } from '../../../../../types';

import UniqueJiraTicketEditor from './UniqueJiraTicketEditor';

const SetupJiraContent: VoidFunctionComponent = () => (
  <div>
    <Text.H3>JIRA Integration Required</Text.H3>
    <span className="issues-modal__body__message">
      <span>
        Your organization has not setup a JIRA integration yet.
        <RouterLink to="/account/settings/integrations/jira">Configure JIRA <Icon icon={icons.externalLink} /></RouterLink>
      </span>
    </span>
  </div>
);

const JiraProjectSettingsFields = [
  'issue_tracker_type',
  'issue_tracker_url',
  'issueTrackerProjectIds',
  'issueTrackerIssueTypes',
  'issueTrackerLabels',
  'issueTrackerCustomFields',
  'issueTrackerComponents',
  'useGlobalTrackerSettings',
] as const;

export type JiraProjectSettings = Simplify<Pick<Project, typeof JiraProjectSettingsFields[number]> & {
  projectJiraSettingsURL: string;
}>;

//  Returns the Jira settings for the given project, if they exist.
type JiraSettingsResult = JiraProjectSettings | { useGlobalTrackerSettings: true };
const getProjectJiraSettings = (project: Project | undefined): JiraSettingsResult => {
  //  For cases where this is being rendered on the global issues page,
  //  there is no "project settings" to pull from, so always just use the global tracker settings.
  if (!project) {
    return { useGlobalTrackerSettings: true };
  }

  return {
    ..._.pick(project, JiraProjectSettingsFields),
    projectJiraSettingsURL: `/projects/${nuc(project.locator as string)}/settings/issues`,
  };
};

type Props = {
  shouldDisableInputs?: boolean;
  issueCategory: Category;
  selectedIssuesCount: number;
  isLoading: boolean;
  setWarningMessage: SetState<string>;
  issues: Issue[];
  setContent: SetState<AnyExportRequest>;
  jiraTicketCount?: number;
  setIsDisabled: SetState<boolean>;
  remediationVersion?: string;
};

const JiraTicketExportContent: VoidFunctionComponent<Props> = ({
  selectedIssuesCount,
  shouldDisableInputs,
  issueCategory,
  isLoading,
  setWarningMessage,
  issues,
  setContent,
  jiraTicketCount,
  setIsDisabled,
  remediationVersion,
}) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const { data: project } = useCurrentProject(isProjectScope(scope));
  const jiras: Jira[] = useSelector(OrganizationsModule.selectors.getCurrentJiras) || [];
  const hasJiras = (jiras?.filter((jira) => jira.enabled)?.length ?? 0) > 0;
  const { trackerType, onTrackerTypeChange } = useTrackerExportPicker(hasJiras, false);
  const [title, setTitle] = useState('');
  const [content, setInternalContent] = useState('');

  const uniqueTicketEditor = useUniqueTicketEditor();

  const { exportOptions, required, optional } = useJiraExport({
    jiras,
    issueCategory,
    projectSettings: getProjectJiraSettings(project) as JiraProjectSettings,
  });

  // If any of the fields update, update the content state at the top level.
  useEffect(() => {
    setContent({
      type: 'jira',
      title,
      description: content,
      uniqueTickets: uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.tickets : [],
      trackerId: exportOptions.project_id,
      jiraId: exportOptions.jira_id,
      issueType: exportOptions.issueType,
      labels: exportOptions.labels,
      customFields: exportOptions.customFields,
      components: exportOptions.components,
    });

    if (content.length > JIRA_CONTENT_MAX_LENGTH) {
      setIsDisabled(true);
    }
  }, [
    content,
    exportOptions,
    setContent,
    title,
    uniqueTicketEditor?.isEnabled,
    uniqueTicketEditor?.tickets,
    setIsDisabled,
  ]);

  const [currentJira] = jiras.filter(jira => jira.id === exportOptions.jira_id);
  useEffect(() => {
    uniqueTicketEditor?.setIsEnabled(currentJira?.defaultUniqueTickets || false);
  },
  // We only want this to run once for a list of issues, so we only depend on
  // the jira issues, not the uniqueTicketEditor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [currentJira]);

  useOnMount(() => {
    // Disable the submit button if there are no configured Jiras.
    if (!hasJiras) {
      setIsDisabled(true);
    }

    // Init single ticket state
    setTitle(BuildTicket.Title(issues, selectedIssuesCount));
    setInternalContent(
      BuildTicket.Content({
        issues,
        issueCategory,
        selectedIssuesCount,
        location: window.location,
        user: window.user as User,
        scope,
        remediationVersion,
      }),
    );

    // Init unique tickets state
    uniqueTicketEditor.setTickets(
      issues.map((issue) => ({
        issueId: issue.id,
        title: BuildTicket.Title([issue], 1),
        content: BuildTicket.Content({
          issues: [issue],
          issueCategory,
          selectedIssuesCount: 1,
          location: window.location,
          user: window.user as User,
          scope,
          remediationVersion,
        }),
      })),
    );

    const issuesWithTicketCount = issues.reduce(
      (accum: number, issue: Issue) => accum + (issue.jiraTickets?.length ?? 0),
      0,
    );

    // Show count only if they have less than max amount of issues allowed selected as we don't load *total* count if more than that
    if ((selectedIssuesCount <= COUNT_MAX_FE && issuesWithTicketCount > 0) || !!jiraTicketCount) {
      const countToUse = jiraTicketCount || issuesWithTicketCount;
      setWarningMessage(
        `${countToUse} selected FOSSA ${pluralizeIssues(countToUse)} will no longer be associated with ${plural(
          countToUse,
          'its',
          'their',
        )} previous Jira ticket.`,
      );
    } else {
      setWarningMessage('');
    }
  });

  // If there are no Jiras set up, display the message and link for configuring.
  if (!hasJiras) {
    return <SetupJiraContent />;
  }

  return (
    <>
      {content?.length > JIRA_CONTENT_MAX_LENGTH && (
        <ExportIssueModalWarning
          message={`JIRA ticket content cannot exceed ${JIRA_CONTENT_MAX_LENGTH} characters. Please edit the content to reduce its length.`}
        />
      )}
      <UniqueJiraTicketEditor uniqueTicketEditor={uniqueTicketEditor} selectedIssuesCount={selectedIssuesCount} />
      <div className="modal-issue-export--layout">
        <ModalIssueExportFields
          required={required}
          optional={optional}
          trackerType={trackerType}
          onTrackerTypeChange={onTrackerTypeChange}
          hasGitHub={false}
          hasJiras={hasJiras}
        />
        <section className="modal-issue-export__content">
          <FormGroup>
            <LabeledTextInput
              labelVariant="legacy"
              label="Title"
              value={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.title : title}
              isDisabled={isLoading || shouldDisableInputs}
              onChange={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.setTitle : setTitle}
            />
            <LabeledTextArea
              labelVariant="legacy"
              label="Content"
              value={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.content : content}
              isDisabled={isLoading || shouldDisableInputs}
              showCharCount
              maxLength={JIRA_CONTENT_MAX_LENGTH}
              onChange={uniqueTicketEditor?.isEnabled ? uniqueTicketEditor.setContent : setInternalContent}
            />
          </FormGroup>
        </section>
      </div>
    </>
  );
};

export default JiraTicketExportContent;
